import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import PasswordStrength, { isPasswordStrong } from "@/components/PasswordStrength";

/** Erzwungener Passwortwechsel-Dialog beim ersten Login.
 *
 * Der Dialog ist nicht schließbar (kein X, kein onOpenChange nach false).
 * Der einzige Ausweg ist ein erfolgreicher Wechsel oder Ausloggen.
 */
export default function ForcePasswordChangeDialog({ open, currentPassword }) {
    const { logout, refresh } = useAuth();
    const navigate = useNavigate();
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!isPasswordStrong(newPw)) {
            toast.error("Neues Passwort erfüllt die Regeln nicht");
            return;
        }
        if (newPw !== confirmPw) {
            toast.error("Die beiden Passwörter stimmen nicht überein");
            return;
        }
        if (currentPassword && newPw === currentPassword) {
            toast.error("Neues Passwort muss sich vom Initial-Passwort unterscheiden");
            return;
        }
        setBusy(true);
        try {
            await api.put("/auth/change-password", {
                current_password: currentPassword,
                new_password: newPw,
            });
            toast.success("Passwort gesetzt. Bitte neu anmelden.");
            await logout();
            navigate("/login", { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Passwortwechsel fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const forceLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <Dialog open={open}>
            <DialogContent
                className="max-w-md [&>button.absolute]:hidden"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                data-testid="force-pw-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-yellow-400" />
                        Bitte legen Sie Ihr Passwort fest
                    </DialogTitle>
                    <DialogDescription>
                        Dies ist Ihr erster Login. Aus Sicherheitsgründen müssen Sie das
                        Initial-Passwort durch ein eigenes ersetzen, bevor Sie fortfahren.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Neues Passwort</Label>
                        <Input
                            type="password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            data-testid="force-pw-new"
                            autoFocus
                        />
                        <PasswordStrength pw={newPw} />
                    </div>
                    <div>
                        <Label>Passwort wiederholen</Label>
                        <Input
                            type="password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            data-testid="force-pw-confirm"
                        />
                        {confirmPw && confirmPw !== newPw && (
                            <p className="text-[11px] text-red-300 mt-1">Passwörter stimmen nicht überein</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-between pt-2">
                    <Button
                        variant="ghost"
                        onClick={forceLogout}
                        data-testid="force-pw-logout"
                        className="text-muted-foreground"
                    >
                        Abbrechen und ausloggen
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !isPasswordStrong(newPw) || newPw !== confirmPw}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="force-pw-submit"
                    >
                        {busy ? "Speichere…" : "Passwort setzen"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
